import { RouteTransitionAnimation, trackClick, useNavigation, useTranslation } from '@helios/frontend-core';
import {
  NavItem,
  PalmettoBottomNav,
  PalmettoBottomNavHostBackgroundColor,
  PalmettoSideNav,
} from '@palmetto/dsm-components';
import { useLocation, useMatches, useNavigate, useSearch } from '@tanstack/react-router';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { kebabCase } from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import { useProperties } from '../../api/properties/useProperties';
import { useUser } from '../../api/user/useUser';
import { useIsSnowing } from '../../api/weather/useWeather';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useKeyboardState } from '../../hooks/useKeyboardState';
import { useLandingRoute } from '../../hooks/useLandingRoute';
import { useNavBadges } from '../../hooks/useNavBadges';
import { useViewport } from '../../hooks/useViewport';

import featureConfig from '../../config/featureConfig';
import { PersistentHomeHero } from '../../pages/HomePageV2/HomeHero/PersistentHomeHero';
import { usePrefetchRiveHome } from '../RiveHome/usePrefetchRiveHome';
import { ROUTE } from '../../routes';
import { currentPageContext } from '../_app/CurrentPageProvider/currentPageContext';
import { useCurrentRouteStaticData } from '../_app/Routes/useCurrentRouteStaticData';
import { ThemeId, useThemeContext } from '../_app/ThemeProvider/ThemeProvider';

import Header from '../Header/Header';
import IntercomBubble from '../IntercomBubble/IntercomBubble';
import { ProgressiveBlur } from '../ProgressiveBlur/ProgressiveBlur';
import SmartBannerAppDownload from '../SmartBannerAppDownload/SmartBannerAppDownload';
import { SunnyChat } from '../SunnyChat/SunnyChat';
import { EXPANDED_WIDTH } from '../SunnyChat/constants';
import { useSunnyChat } from '../SunnyChat/useSunnyChat';
import { CollapsibleRiveBottomNav } from './CollapsibleRiveBottomNav';
import { RiveBottomNavArtboard } from './RiveBottomNav';
import { settledBottomNavItemContext } from './settledBottomNavItemContext';
import { bottomNav, navConfig, sideNavBottom, sideNavTop } from './navConfig';
import { useScrollCollapse } from './useScrollCollapse';

const MIN_BOTTOM_NAV_ITEMS = 3;
const RIVE_BOTTOM_NAV_IDS = ['home', 'energy', 'learn', 'shop'];
const SUNNY_CHAT_HORIZONTAL_MARGIN_PX = 28;

const mapBackgroundColorToCssVar = (backgroundColor?: string): string => {
  const token = backgroundColor?.replace(/^bg-/, '');
  return token ? `var(--${token})` : 'var(--canvas-bold)';
};

const mapBackgroundColor = (backgroundColor?: string): PalmettoBottomNavHostBackgroundColor => {
  switch (backgroundColor) {
    case 'bg-canvas-bold':
      return 'bold';
    case 'bg-canvas-bolder':
      return 'bolder';
    case 'bg-canvas-inverted':
      return 'inverted';
    case 'bg-canvas-default':
      return 'default';
    default:
      return 'bold';
  }
};

const matchesRoutePattern = (pathname: string, pattern: string): boolean => {
  const pathnameSegments = pathname.split('/');
  return pattern
    .split('/')
    .every((segment, index) =>
      segment.startsWith('$') ? Boolean(pathnameSegments[index]) : segment === pathnameSegments[index],
    );
};

const getRouteTransitionAnimation = (
  targetItemIndex: number,
  currentItemIndex: number,
): RouteTransitionAnimation | undefined => {
  let routeTransitionAnimation: RouteTransitionAnimation | undefined = undefined;

  const doBothIndicesExist = targetItemIndex !== -1 && currentItemIndex !== -1;

  if (doBothIndicesExist) {
    if (targetItemIndex > currentItemIndex) {
      routeTransitionAnimation = 'nav-slide-in-from-right';
    } else if (targetItemIndex < currentItemIndex) {
      routeTransitionAnimation = 'nav-slide-in-from-left';
    }
  }
  return routeTransitionAnimation;
};

export const MainNavLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useUser();
  const flags = useFlags();
  const { data: property } = useProperties(user?.propertyIds?.[0]);
  const isHomePageV2 = featureConfig.displayHomePageV2(user, flags);
  const { isSnowing, isLoading: isWeatherLoading } = useIsSnowing(user?.propertyIds?.[0]);
  usePrefetchRiveHome(isHomePageV2 && !isWeatherLoading, isSnowing, property?.address?.coordinates);
  const { openURL } = useNavigation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hideNav, hideAppBanner } = useSearch({ strict: false });
  const { t } = useTranslation();
  const { isKeyboardOpen } = useKeyboardState();
  const currentRouteStaticData = useCurrentRouteStaticData();
  const { theme } = useThemeContext();
  const landingRoute = useLandingRoute();
  const { safeAreaTopColor, isSafeAreaTopFullBleed } = useContext(currentPageContext);
  const matches = useMatches();
  // A release-flag kill switch, not a route/experience gate - see `featureConfig.sunnyChatEnabled`'s
  // own comment and `useSunnyChat`'s `enabled` param. Disabling it falls back to the
  // pre-SunnyChat nav (no collapse) and Intercom's own default launcher (see the
  // `IntercomBubble` usage below).
  const sunnyChatEnabled = featureConfig.sunnyChatEnabled(user, flags);
  const sunnyChat = useSunnyChat(matches.at(-1)?.routeId, sunnyChatEnabled);
  const isMobileViewport = !useIsDesktop();
  const { width: viewportWidth } = useViewport();
  const mobileSunnyChatMaxWidth = Math.min(viewportWidth - SUNNY_CHAT_HORIZONTAL_MARGIN_PX * 2, EXPANDED_WIDTH);

  const effectiveSafeAreaTopColor = currentRouteStaticData?.safeAreaTopColor ?? safeAreaTopColor ?? null;

  const navBadges = useNavBadges({ property });

  const defaultActiveNavItemId = landingRoute === ROUTE.ENERGY ? 'energy' : 'home';

  const activeNavItemId = useMemo(() => {
    const matchedId = Object.entries(navConfig).find(([, { path, matchPaths }]) =>
      (matchPaths ?? [path]).some((pattern) => matchesRoutePattern(pathname, pattern)),
    )?.[0];
    return matchedId === undefined || matchedId === 'home' ? defaultActiveNavItemId : matchedId;
  }, [pathname, defaultActiveNavItemId]);

  const handleNavClick = (id: string, navType: 'bottom' | 'side') => {
    trackClick(`${navType}-nav-${kebabCase(id)}`, 'link', {
      href: navConfig[id].path,
      page: window.location.href,
    });

    const { path } = navConfig[id];

    if (navType === 'bottom') {
      navigate({ to: path, viewTransition: false });
      return;
    }

    const targetItemIndex = bottomNavItems.findIndex((item) => item.id === id);
    const currentItemIndex = bottomNavItems.findIndex((item) => item.id === bottomNavActiveItemId);

    const routeTransitionAnimation = getRouteTransitionAnimation(targetItemIndex, currentItemIndex);

    openURL(path, routeTransitionAnimation);
  };

  const createNavItem = (id: string, navType: 'bottom' | 'side'): NavItem => {
    const navItem: NavItem = {
      id,
      iconName: navConfig[id].iconName,
      label: t(`navigation.${id}`),
      as: 'button',
      onClick: () => handleNavClick(id, navType),
    };

    if (navBadges[id as keyof typeof navBadges]) {
      const badge = navBadges[id as keyof typeof navBadges];
      if (badge) {
        navItem.badge = badge;
      }
    }

    return navItem;
  };

  const createNavItems = (ids: string[], navType: 'bottom' | 'side'): NavItem[] => {
    const items: NavItem[] = ids
      .filter((id) => {
        const config = navConfig[id];
        return config.featureConfig === undefined || config.featureConfig(user, flags, property);
      })
      .map((id) => createNavItem(id, navType));

    if (items.length < MIN_BOTTOM_NAV_ITEMS && navType === 'bottom') {
      const middleIndex = Math.floor(items.length / 2);
      items.splice(middleIndex, 0, createNavItem('energyProfile', 'bottom'));
    }
    return items;
  };

  const bottomNavItems = useMemo(
    () => createNavItems(bottomNav, 'bottom'),
    [bottomNav, user, flags, property, pathname, navBadges],
  );
  const bottomNavActiveItemId = bottomNavItems.some(({ id }) => id === activeNavItemId)
    ? activeNavItemId
    : defaultActiveNavItemId;
  const [riveSettledBottomNavItemId, setRiveSettledBottomNavItemId] = useState(bottomNavActiveItemId);
  const sideNavTopItems = useMemo(
    () => createNavItems(sideNavTop, 'side'),
    [sideNavTop, user, flags, property, pathname, navBadges],
  );
  const sideNavBottomItems = useMemo(
    () => createNavItems(sideNavBottom, 'side'),
    [sideNavBottom, user, flags, property, navBadges],
  );

  const showRiveBottomNav = featureConfig.riveBottomNav(user, flags);

  const canNavCollapse =
    showRiveBottomNav && isMobileViewport && sunnyChat.shouldRender && sunnyChat.mode !== 'launcher';
  const { isCollapsed: isNavCollapsed, expand: expandNav } = useScrollCollapse(canNavCollapse, pathname);

  const handleCollapsedNavClick = () => {
    trackClick('bottom-nav-expand', 'button', { page: window.location.href });
    expandNav();
  };

  const riveBottomNavArtboard: RiveBottomNavArtboard =
    theme.id === ThemeId.palmettoDark ? 'Bottom Nav - Dark' : 'Bottom Nav - Light';

  // Hides each piece of nav chrome below individually, from within this one render tree,
  // rather than early-returning a separate, differently-shaped tree.
  const hideChrome = !isAuthenticated || currentRouteStaticData?.hideNavigation || hideNav;
  const isRiveBottomNavRendered = showRiveBottomNav && !hideChrome && !currentRouteStaticData?.bottomNav?.hide;
  const settledBottomNavItemId = isRiveBottomNavRendered ? riveSettledBottomNavItemId : bottomNavActiveItemId;

  return (
    <div
      id="main-layout"
      data-theme={currentRouteStaticData?.theme || 'core'}
      className={`flex h-[100svh] ${
        currentRouteStaticData?.backgroundColor || 'bg-canvas-bold'
      } pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)] overflow-hidden`}
    >
      <div
        className={`flex min-h-full min-w-full [view-transition-name:main-content] md:[view-transition-name:null] ${
          currentRouteStaticData?.backgroundColor || 'bg-canvas-bold'
        }`}
      >
        {!hideChrome && (
          <div
            data-theme={currentRouteStaticData?.navTheme}
            className="hidden md:flex p-24 pr-[0px] [&_ul]:!pl-[0px] z-50"
          >
            {/* Desktop Side Nav */}
            <PalmettoSideNav
              className="!h-full bg-canvas-default hidden lg:flex"
              collapsed={false}
              topItems={sideNavTopItems}
              bottomItems={sideNavBottomItems}
              activeItemId={activeNavItemId}
            />
            {/* Tablet Side Nav */}
            <PalmettoSideNav
              className="!h-full bg-canvas-default hidden md:flex lg:hidden"
              collapsed={true}
              topItems={sideNavTopItems}
              bottomItems={sideNavBottomItems}
              activeItemId={activeNavItemId}
            />
          </div>
        )}
        <div className="flex flex-col flex-1 overflow-hidden">
          {!hideChrome && !isSafeAreaTopFullBleed && (
            <div
              data-theme={currentRouteStaticData?.safeAreaTopTheme}
              className={`h-[env(safe-area-inset-top)] w-full ${
                effectiveSafeAreaTopColor || currentRouteStaticData?.backgroundColor || 'bg-canvas-bold'
              }`}
              style={{
                borderTop: `24px solid ${
                  effectiveSafeAreaTopColor || currentRouteStaticData?.backgroundColor || 'bg-canvas-bold'
                }`,
              }}
            ></div>
          )}

          {!hideChrome && <Header />}
          {!hideChrome && !hideAppBanner && <SmartBannerAppDownload />}
          {!hideChrome && <IntercomBubble user={user} sunnyChatEnabled={sunnyChatEnabled} />}
          <settledBottomNavItemContext.Provider value={settledBottomNavItemId}>
            <PersistentHomeHero enabled={isHomePageV2}>{children}</PersistentHomeHero>
          </settledBottomNavItemContext.Provider>
        </div>
        {!hideChrome && !currentRouteStaticData?.bottomNav?.hide && (
          <div data-theme={currentRouteStaticData?.bottomNav?.hostTheme}>
            <ProgressiveBlur
              className={`md:hidden fixed bottom-[0px] left-[0px] right-[0px] z-40 pointer-events-none transition-all duration-0 ${
                isKeyboardOpen ? '!-bottom-[100px]' : 'duration-500'
              }`}
              height="88px"
              gradientColor={mapBackgroundColorToCssVar(currentRouteStaticData?.backgroundColor)}
            />
          </div>
        )}
        {!hideChrome && sunnyChat.shouldRender && (
          <>
            <div className="hidden md:block lg:hidden fixed bottom-24 right-24 z-50">
              <SunnyChat {...sunnyChat} trackingId="sunny-chat" maxWidth={EXPANDED_WIDTH} fillBarWidth />
            </div>
            <div className="hidden lg:block fixed bottom-24 right-24 z-50">
              <SunnyChat {...sunnyChat} trackingId="sunny-chat" maxWidth={EXPANDED_WIDTH} fillBarWidth />
            </div>
          </>
        )}
        {!hideChrome &&
          currentRouteStaticData?.bottomNav?.hide &&
          !currentRouteStaticData?.hideSunnyChatOnMobile &&
          sunnyChat.shouldRender && (
            <div
              className={classNames(
                'md:hidden pointer-events-none fixed bottom-[23px] z-10 flex min-[680px]:bottom-[20px]',
                sunnyChat.mode === 'launcher'
                  ? 'right-[28px] justify-end'
                  : 'left-[28px] right-[28px] justify-center min-[680px]:left-1/2 min-[680px]:right-auto min-[680px]:-translate-x-1/2',
              )}
            >
              <SunnyChat
                {...sunnyChat}
                trackingId="sunny-chat"
                maxWidth={mobileSunnyChatMaxWidth}
                fillBarWidth
                className="pointer-events-auto"
              />
            </div>
          )}
      </div>
      {!hideChrome && !currentRouteStaticData?.bottomNav?.hide && (
        <div data-theme={currentRouteStaticData?.bottomNav?.hostTheme}>
          {showRiveBottomNav ? (
            <div
              className={`md:hidden fixed bottom-[0px] left-[0px] right-[0px] z-50 transition-all duration-0 ${
                isKeyboardOpen ? '!-bottom-[100px]' : 'duration-500'
              } [view-transition-name:bottom-nav] px-[clamp(0px,(100%_-_295px)/2,40px)] pb-[max(34px,env(safe-area-inset-bottom))]`}
            >
              <CollapsibleRiveBottomNav
                sunnyChat={sunnyChat}
                isCollapsed={isNavCollapsed}
                artboard={riveBottomNavArtboard}
                activeItemId={bottomNavActiveItemId}
                onNavClick={(id) => handleNavClick(id, 'bottom')}
                onCollapsedNavClick={handleCollapsedNavClick}
                onTabSettled={setRiveSettledBottomNavItemId}
              />
            </div>
          ) : (
            <PalmettoBottomNav
              className={`md:hidden transition-all duration-0 ${
                isKeyboardOpen ? '!-bottom-[100px]' : 'duration-500'
              } [view-transition-name:bottom-nav] ![background-image:none] px-[clamp(0px,(100%_-_295px)/2,40px)]! [&>div]:min-w-[295px]!`}
              hostBackgroundColor={mapBackgroundColor(currentRouteStaticData?.backgroundColor)}
              items={bottomNavItems}
              activeItemId={bottomNavActiveItemId}
              navTheme="core"
              {...currentRouteStaticData?.bottomNav?.props}
            />
          )}
        </div>
      )}
    </div>
  );
};
